import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Skeleton } from '@mui/material'

import { useGetTrackedGameLiveEventBlueprintsQuery } from '../../../../../api/core'

type BlueprintEventCountProps = {
  blueprintId: string
}

/**
 * Lazily resolves the number of distinct events recorded for a game-level blueprint.
 *
 * There is no aggregate endpoint for event counts per blueprint, so the count is
 * fetched per blueprint only when this component mounts (i.e. when a game section
 * is expanded in the overview). Unpublished games may not resolve through the
 * customer-facing endpoint, in which case a placeholder is shown.
 */
export const BlueprintEventCount: FC<BlueprintEventCountProps> = ({ blueprintId }) => {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useGetTrackedGameLiveEventBlueprintsQuery({ blueprintId, type: 'all' })

  const eventCount = useMemo(() => {
    if (!data) return undefined
    return new Set(data.evolution.map((entry) => entry.eventId)).size
  }, [data])

  if (isLoading) {
    return <Skeleton variant="rounded" width={70} height={24} sx={{ display: 'inline-block' }} />
  }

  if (isError || eventCount === undefined) {
    return <>–</>
  }

  return <>{t('internal-live-events:event_count', { count: eventCount })}</>
}
